import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null); // null = checker, false = ikke logget ind
  const { pathname } = useLocation();
  const idleTimer = useRef(null);
  const lastRenewal = useRef(0);

  useEffect(() => {
    if (!pathname.startsWith("/admin") || pathname === "/admin/login" || pathname === "/admin/crm" || pathname.startsWith("/admin/crm/")) {
      setAdmin(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setAdmin(r.data))
      .catch(() => setAdmin(false));
  }, [pathname]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdmin(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setAdmin(false);
    }
  }, []);

  const stopIdleTimer = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = null;
  }, []);

  const renewAfterActivity = useCallback(async () => {
    if (!admin || pathname === "/admin/login") return;
    const now = Date.now();
    if (now - lastRenewal.current < 15000) return;
    lastRenewal.current = now;
    try {
      const { data } = await api.post("/auth/refresh");
      setAdmin(data.user);
    } catch {
      setAdmin(false);
    }
  }, [admin, pathname]);

  useEffect(() => {
    if (!admin || pathname === "/admin/login") {
      stopIdleTimer();
      return undefined;
    }
    const timeoutMinutes = Number(admin.idle_timeout_minutes) || 5;
    const resetIdleTimer = () => {
      stopIdleTimer();
      idleTimer.current = window.setTimeout(() => logout(), timeoutMinutes * 60 * 1000);
      renewAfterActivity();
    };
    const events = ["click", "keydown", "scroll", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      stopIdleTimer();
      events.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
  }, [admin, logout, pathname, renewAfterActivity, stopIdleTimer]);

  return <AuthContext.Provider value={{ admin, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
